// Задания, достижения, «Посылка от дяди Вити» и почта — tech/03 §9 (E09-T01, E09-T02, E09-T06). Прогресс считает
// сервер по доменным событиям; клиент не сообщает «я сделал задание».
import { z } from 'zod';
import { BigIdString, Grant, IsoTime, NextCursor, strict } from '../common.js';
import { endpoint } from '../endpoint.js';
import { Drop } from './inventory.js';
import { Wallet } from './models.js';

export const QuestPeriod = z.enum(['daily', 'weekly']);
const QuestId = z.string().regex(/^Q-[DW]-[0-9]{2}$/);
const AchievementId = z.string().regex(/^A-[0-9]{2}$/);

export const Quest = strict({
  questId: QuestId,
  /** Ключ периода: `D:YYYY-MM-DD` или `W:YYYY-MM-DD` (понедельник) — передаётся в claim. */
  periodKey: z.string(),
  slot: z.int().min(1).max(10).nullable(),
  category: z.string(),
  progress: z.int().nonnegative(),
  target: z.int().positive(),
  status: z.enum(['active', 'completed', 'claimed']),
  rewards: Grant,
  resetsAt: IsoTime,
});

export const getQuests = endpoint({
  id: 'quests.list', method: 'GET', path: '/quests', auth: true, signed: false, idempotency: 'read', rate: 'read',
  query: strict({ period: QuestPeriod.optional() }),
  response: strict({
    items: z.array(Quest),
    /** Бонус «все 5» ежедневных (ГД 09 §2). */
    allFive: strict({ done: z.int().nonnegative(), rewards: Grant, claimed: z.boolean() }).nullable(),
    dailyBox: strict({ available: z.boolean(), nextAt: IsoTime }),
  }), status: 200, errors: [], spec: 'tech/03 §9; ГД 09 §2–§3', task: 'E09-T01',
});

/** Награда задания; `questId = 'ALL5'` — бонус «все 5» дня (Р-68). */
export const claimQuest = endpoint({
  id: 'quests.claim', method: 'POST', path: '/quests/{questId}/claim', auth: true, signed: true, idempotency: 'natural', rate: 'write',
  params: strict({ questId: z.union([QuestId, z.literal('ALL5')]) }), body: strict({ periodKey: z.string() }),
  response: strict({ rewards: Grant, wallet: Wallet }), status: 200, errors: ['ALREADY_CLAIMED', 'NOT_COMPLETED', 'NOT_FOUND'], spec: 'tech/03 §9', task: 'E09-T01',
});

export const Achievement = strict({
  achievementId: AchievementId,
  /** Текущая ступень к получению (1…), null — все ступени взяты. */
  tier: z.int().positive().nullable(),
  tiers: z.int().positive(),
  progress: z.int().nonnegative(),
  target: z.int().positive().nullable(),
  achievedAt: IsoTime.nullable(),
  claimable: z.boolean(),
  rewards: Grant.nullable(),
  claimedTiers: z.int().nonnegative(),
});

export const getAchievements = endpoint({
  id: 'achievements.list', method: 'GET', path: '/achievements', auth: true, signed: false, idempotency: 'read', rate: 'read',
  response: strict({ items: z.array(Achievement) }), status: 200, errors: [], spec: 'tech/03 §9; ГД 09 §5', task: 'E09-T06',
});

/** Забрать следующую ступень достижения (ступени — строго по порядку). */
export const claimAchievement = endpoint({
  id: 'achievements.claim', method: 'POST', path: '/achievements/{achievementId}/claim', auth: true, signed: true, idempotency: 'natural', rate: 'write',
  params: strict({ achievementId: AchievementId }), body: strict({ tier: z.int().positive() }),
  response: strict({ rewards: Grant, wallet: Wallet, achievement: Achievement }), status: 200,
  errors: ['ALREADY_CLAIMED', 'NOT_COMPLETED', 'NOT_FOUND'], spec: 'tech/03 §9; economy/04 §7.5', task: 'E09-T06',
});

/** «Посылка от дяди Вити» — бесплатный ящик дня (economy/04 §7.3). */
export const claimDailyBox = endpoint({
  id: 'daily_box.claim', method: 'POST', path: '/daily-box/claim', auth: true, signed: true, idempotency: 'natural', rate: 'write',
  body: strict({}), response: strict({ drops: z.array(Drop), wallet: Wallet }), status: 200, errors: ['ALREADY_CLAIMED'], spec: 'tech/03 §9; economy/04 §7.3', task: 'E09-T01',
});

export const MailItem = strict({
  mailId: BigIdString,
  kind: z.string().max(32),
  titleKey: z.string().max(64),
  params: z.record(z.string(), z.unknown()),
  attachments: Grant,
  createdAt: IsoTime,
  expiresAt: IsoTime.nullable(),
  claimedAt: IsoTime.nullable(),
});

export const getMail = endpoint({
  id: 'mail.list', method: 'GET', path: '/mail', auth: true, signed: false, idempotency: 'read', rate: 'read',
  query: strict({ cursor: z.string().max(256).optional() }),
  response: strict({ items: z.array(MailItem), nextCursor: NextCursor, unclaimed: z.int().nonnegative() }), status: 200, errors: [], spec: 'tech/03 §9', task: 'E09-T02',
});

export const claimMail = endpoint({
  id: 'mail.claim', method: 'POST', path: '/mail/{mailId}/claim', auth: true, signed: true, idempotency: 'natural', rate: 'write',
  params: strict({ mailId: BigIdString }), body: strict({}), response: strict({ rewards: Grant, wallet: Wallet }), status: 200,
  errors: ['ALREADY_CLAIMED', 'GONE', 'NOT_FOUND'], spec: 'tech/03 §9', task: 'E09-T02',
});
